'use strict';

const {
    buildRequestImp,
    buildProductRequestImp,
    buildFeaturedBlockRequestImp,
    buildProductTileBannerRequestImp,
    buildRequestObject,
    buildHomepageRecommendationProductsRequestImp,
    buildPDPProductsSliderRequestImp
} = require('./helpers/adformRequestBuilderHelpers');
const responseHandler = require('./helpers/adformBidsResponseHandler');
const productResponseHandler = require('./helpers/adformProductsBidsResponseHandler');
const featuredBlockResponseHandler = require('./helpers/adformFeaturedBlockBidsResponseHandler');
const listPageResponseHandler = require('./helpers/adformListPageBannerBidsResponseHandler');
const homepageRecommendedProductsResponseHandler = require('./helpers/homepageRecommendedProductsResponseHandler');
const pdpProductsSliderResponseHandler = require('./helpers/pdpProductsSliderResponseHandler');
const adformConfigValidator = require('./helpers/adformConfigValidator');
const adformConstants = require('./adformConstants');
const httpCodes = require('../../../../../../app_progamers_core/cartridge/client/default/js/common/httpCodes');
const { getBuyerUid } = require('./common/adformHelperFunctions');

/**
 * Get page property for adform request.
 * This way, we get ads for specific page
 * @return {string} page (e.g. gaming)
 */
function getSitePage() {
    return window.categorySlugPath || '';
}

/**
 * Get customer's ip address (for adform request).
 * @return {string} ip address
 */
function getIp() {
    // for non prod env, we use whitelisted fixed IP (otherwise for some requests with we get 204 no data)
    if (window.envNotProduction) {
        return adformConstants.IP_ADDRESS_FALLBACK;
    }

    let ip = window.customerIpAddress;

    return /\d{1,3}\.\d{1,3}\.\d{1,3}.\d{1,3}/.test(ip) ? ip : adformConstants.IP_ADDRESS_FALLBACK;
}

/**
 * Wraps fetch with a timeout, aborting the request if it takes too long.
 * An aborted request rejects like any other fetch error, so it flows into the existing .catch handling.
 * @param {string} url - request url
 * @param {Object} options - fetch options
 * @return {Promise} - fetch promise
 */
function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), adformConstants.FETCH_TIMEOUT_MS);

    return fetch(url, { ...options, signal: controller.signal })
        .finally(() => clearTimeout(timeoutId));
}

/**
 * Builds object used for adform request.
 * @param {string} requestSitePage - part of request
 * @param {Array} imp - part of request
 * @return {Object|null} - data for adform request
 */
function getRequestData(requestSitePage, imp) {
    return buildRequestObject({
        sitePage: requestSitePage,
        siteDomain: window.location.hostname,
        buyeruid: getBuyerUid(),
        deviceUA: navigator.userAgent,
        deviceIP: getIp(),
        imp: imp
    });
}

/**
 * Calls adform api to get bids based on containerType (banner, category, subcategory...).
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 * @param {string} requestSitePage - request param for adform request (to get ads for specific page)
 * @param {Object} additionalEventData - additional data needed to process adform data
 */
function fetchBids(
    containerTypeConfig,
    requestSitePage,
    additionalEventData = {}
) {
    const requestData = getRequestData(
        requestSitePage,
        buildRequestImp(containerTypeConfig)
    );
    if (!requestData) {
        responseHandler.handleInvalidRequest(containerTypeConfig, additionalEventData);
        return;
    }

    fetchWithTimeout(adformConstants.FETCH_BIDS_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
    })
        .then(response=> {
            if (response.ok && response.status === httpCodes.OK_200) {
                return response.json();
            }

            return null;
        })
        .then(data => {
            if (data) {
                responseHandler.handleBidsResponse(containerTypeConfig, data, additionalEventData);
            } else {
                responseHandler.handleNoBids(containerTypeConfig, additionalEventData);
            }
        })
        .catch(error => {
            responseHandler.handleError(containerTypeConfig, error, additionalEventData);
        });
}

/**
 * Calls adform api to get bids for category slider.
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {Object} containerTypeConfig - config in adformConstants.js holding tagid, img sizes and similar (e.g. CONTAINER_TYPE_CATEGORY_CONFIG)
 */
function fetchCategorySliderBids(containerTypeConfig) {
    if (!adformConfigValidator.checkMandatoryFields(containerTypeConfig)) {
        responseHandler.handleInvalidRequest(containerTypeConfig);
        return;
    }

    fetchBids(
        containerTypeConfig,
        getSitePage()
    );
}

/**
 * Calls adform api to get bids for homepage slider.
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {Object} additionalEventData - additional data needed to process adform data (e.g. slider element)
 */
function fetchHomepageSliderBids(additionalEventData) {
    const containerTypeConfig = adformConstants.CONTAINER_TYPE_HOMEPAGE_CONFIG;
    if (!adformConfigValidator.checkMandatoryFields(containerTypeConfig)) {
        responseHandler.handleInvalidRequest(containerTypeConfig, additionalEventData);
        return;
    }

    fetchBids(
        containerTypeConfig,
        adformConstants.HOMEPAGE_REQUEST_SITE_PAGE,
        additionalEventData
    );
}

/**
 * Calls adform api to get bids for navigation.
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {Element} megaMenuAdWrapper - ancestor element of mega menu ad
 * @param {string} requestSitePage - to get bids specific to that page
 */
function fetchMegaMenuBid(megaMenuAdWrapper, requestSitePage) {
    const additionalEventData = {
        megaMenuAdWrapper: megaMenuAdWrapper
    };
    const typeConfig = adformConstants.CONTAINER_TYPE_NAV_CONFIG;
    if (!adformConfigValidator.isContainerTypeConfigValidForNonSlider(typeConfig)) {
        responseHandler.handleInvalidRequest(typeConfig, additionalEventData);
        return;
    }

    fetchBids(
        typeConfig,
        requestSitePage,
        additionalEventData
    );
}

/**
 * Calls adform api to get bids for product tiles.
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 */
function fetchProductBids() {
    const requestData = getRequestData(
        getSitePage(),
        buildProductRequestImp(adformConstants.CATEGORIES_SPONSORED_PRODUCT_ADS_TAG_ID)
    );
    if (!requestData) {
        productResponseHandler.handleInvalidRequest();
        return;
    }

    fetchWithTimeout(adformConstants.FETCH_BIDS_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
    })
        .then(response=> {
            if (response.ok && response.status === httpCodes.OK_200) {
                return response.json();
            }

            return null;
        })
        .then(data => {
            if (data) {
                productResponseHandler.handleBidsResponse(data);
            } else {
                productResponseHandler.handleNoBids();
            }
        })
        .catch(error => {
            productResponseHandler.handleError(error);
        });
}

/**
 * Calls adform api to get bids for "featured block" (component with banner and products).
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {string} tagId - request tagId param
 * @param {Element} wrapper - wrapper element (in case of bid, we insert it here)
 * @param {string} bannerPosition - if "featured block" banner should be "left" or "right"
 */
function fetchFeaturedBlock(tagId, wrapper, bannerPosition) {
    const additionalEventData = {
        wrapper: wrapper,
        bannerPosition: bannerPosition
    };

    const requestData = getRequestData(
        adformConstants.HOMEPAGE_REQUEST_SITE_PAGE,
        buildFeaturedBlockRequestImp(tagId)
    );
    if (!requestData) {
        featuredBlockResponseHandler.handleInvalidRequest(additionalEventData);
        return;
    }

    fetchWithTimeout(adformConstants.FETCH_BIDS_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
    })
        .then(response=> {
            if (response.ok && response.status === httpCodes.OK_200) {
                return response.json();
            }

            return null;
        })
        .then(data => {
            if (data) {
                featuredBlockResponseHandler.handleBidsResponse(data, additionalEventData);
            } else {
                featuredBlockResponseHandler.handleNoBids(additionalEventData);
            }
        })
        .catch(error => {
            featuredBlockResponseHandler.handleError(error, additionalEventData);
        });
}

/**
 * Calls adform api to get bids for "listpage abnner slots" (component with banner as producttile).
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {string} tagId - request tagId param
 * @param {Element} wrappers - wrappers element (in case of bid, we insert it here)
 */
function fetchProductTileBanner(tagId, wrappers) {
    const additionalEventData = {
        wrappers: wrappers
    };
    const requestData = getRequestData(
        adformConstants.LISTPAGE_REQUEST_SITE_PAGE,
        buildProductTileBannerRequestImp(tagId)
    );
    if (!requestData) {
        return;
    }

    fetchWithTimeout(adformConstants.FETCH_BIDS_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
    })
        .then(response=> {
            if (response.ok && response.status === httpCodes.OK_200) {
                return response.json();
            }

            return null;
        })
        .then(data => {
            if (data) {
                listPageResponseHandler.handleBidsResponse(data, additionalEventData);
            }
        })
        .catch(error => {
            listPageResponseHandler.handleError(error, additionalEventData);
        });
}

/**
 * Calls adform api to get bids for "homepage recommendation products slider".
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {Element} slider - slider element (in case of bid, we insert products here)
 */
function fetchHomepageRecommendedProducts(slider) {
    const additionalEventData = {
        slider: slider
    };

    const requestData = getRequestData(
        adformConstants.HOMEPAGE_REQUEST_SITE_PAGE,
        buildHomepageRecommendationProductsRequestImp()
    );
    if (!requestData) {
        homepageRecommendedProductsResponseHandler.handleInvalidRequest(additionalEventData);
        return;
    }

    fetchWithTimeout(adformConstants.FETCH_BIDS_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
    })
        .then(response=> {
            if (response.ok && response.status === httpCodes.OK_200) {
                return response.json();
            }

            return null;
        })
        .then(data => {
            if (data) {
                homepageRecommendedProductsResponseHandler.handleBidsResponse(data, additionalEventData);
            } else {
                homepageRecommendedProductsResponseHandler.handleNoBids(additionalEventData);
            }
        })
        .catch(error => {
            homepageRecommendedProductsResponseHandler.handleError(error, additionalEventData);
        });
}

/**
 * Calls adform api to get bids for "pdp slider products".
 * If there are bids in response, calls handler helper functions to process and place bids in right place.
 * @param {string} tagId - request tagId param
 * @param {string} pdpCategoryUrl - category url, used for adform request
 */
function fetchPDPProductsSlider(tagId, pdpCategoryUrl) {
    const requestData = getRequestData(
        getSitePage(pdpCategoryUrl),
        buildPDPProductsSliderRequestImp(tagId)
    );
    if (!requestData) {
        pdpProductsSliderResponseHandler.handleInvalidRequest();
        return;
    }

    fetchWithTimeout(adformConstants.FETCH_BIDS_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
    })
        .then(response=> {
            if (response.ok && response.status === httpCodes.OK_200) {
                return response.json();
            }

            return null;
        })
        .then(data => {
            if (data) {
                pdpProductsSliderResponseHandler.handleBidsResponse(data);
            } else {
                pdpProductsSliderResponseHandler.handleNoBids();
            }
        })
        .catch(error => {
            pdpProductsSliderResponseHandler.handleError(error);
        });
}

module.exports = {
    fetchCategorySliderBids: fetchCategorySliderBids,
    fetchHomepageSliderBids: fetchHomepageSliderBids,
    fetchMegaMenuBid: fetchMegaMenuBid,
    fetchProductBids: fetchProductBids,
    fetchFeaturedBlock: fetchFeaturedBlock,
    fetchProductTileBanner: fetchProductTileBanner,
    fetchHomepageRecommendedProducts: fetchHomepageRecommendedProducts,
    fetchPDPProductsSlider: fetchPDPProductsSlider
};
